import React, { useState, useContext, memo } from 'react';
import tw from 'twin.macro';
import { CommentFill } from './CommentFill';
import {
  CommentsContext,
  CommentContext,
  PreferenceContext,
  UserContext,
  ModerationContext,
} from '../context';
import { Icon } from '../icon';
import { ArrowFullIcon } from 'icons';
import { gaEvent } from 'services/ga';
import { Small } from '../text';

const topThreeSubComments = (hasHiddenChildComments, comments) => {
  const newComments = hasHiddenChildComments ? [...comments] : [];
  const commentsSortedOnKudos = [
    ...newComments
      .reverse()
      .sort((a, b) => (a.kudos_count < b.kudos_count ? 1 : -1))
      .map((comment) => comment.id)
      .slice(0, 3),
  ];
  return commentsSortedOnKudos;
};

const ToggleChildComments = ({ childComments, numbOfChildCommentsShowing }) => {
  const { showAllChildComments, setShowAllChildComments } = useContext(CommentContext);
  const numOfHiddenComments = childComments.length - numbOfChildCommentsShowing;
  const plural = numOfHiddenComments > 1 ? 's' : '';
  return (
    <div
      tw='pt-2 flex items-center cursor-pointer text-brand'
      onClick={() => {
        gaEvent('dmp_comment', (showAllChildComments ? 'hide' : 'show') + '_child_comments');
        setShowAllChildComments(!showAllChildComments);
      }}
    >
      <Icon
        Type={ArrowFullIcon}
        size='small'
        animate
        css={[
          tw`text-brand mr-2 transition-transform rotate-0`,
          showAllChildComments && tw`rotate-180`,
        ]}
      />
      <Small
        text={`${numOfHiddenComments} reaguursel${plural} ${showAllChildComments ? 'verbergen' : 'verborgen'}`}
      />
    </div>
  );
};

export const Comment = memo(function Comment({ comment }) {
  const { user } = useContext(UserContext);
  const { moderationState } = useContext(ModerationContext) || {};
  const { lastAddedComment, moderatedTime, showAllComments } = useContext(CommentsContext);
  const {
    preference: {
      sorting: { comments: commentsSort },
    },
  } = useContext(PreferenceContext);
  const userCanModerate = user && user.moderate;
  const activeModerationComments =
    showAllComments && userCanModerate && commentsSort === 'Moderator view';
  const [showAllChildComments, setShowAllChildComments] = useState(
    activeModerationComments || false,
  );

  const numbOfChildCommentsShowing = 3;
  const hasHiddenChildComments = comment.child_comments && comment.child_comments.length > 3;

  const moderatedAt = new Date(moderatedTime);
  const commentCreatedAt = new Date(comment.creation_datetime);
  const isModerated = moderatedAt >= commentCreatedAt;

  const commentRemoved = userCanModerate && moderationState.removedList.includes(comment.id);
  const commentBanned = userCanModerate && moderationState.bannedList.includes(comment.id);
  const commentHidden = userCanModerate && moderationState.hiddenList.includes(comment.id);

  return (
    <CommentContext.Provider
      value={{
        showAllChildComments,
        setShowAllChildComments,
      }}
    >
      <div
        css={[
          tw`flex flex-col rounded-lg pr-0`,
          (comment.newComment || comment.id === Number(lastAddedComment)) &&
            tw`bg-info p-3 bg-opacity-20`,
          commentRemoved && tw`bg-warning bg-opacity-20 pb-3 px-3 pt-2 border-l-4 border-warning`,
          commentBanned && tw`bg-danger bg-opacity-20 pb-3 px-3 pt-2 border-l-4 border-danger`,
          commentHidden && tw`bg-icons bg-opacity-20 pb-3 px-3 pt-2 border-l-4 border-icons`,
        ]}
        className='group'
        id={`comment${comment.id}`}
      >
        <div
          css={[
            comment.top_comment && tw`bg-brand p-3 bg-opacity-30 -mb-2 rounded-lg`,
            userCanModerate && commentsSort === 'Moderator view' && isModerated && tw`opacity-50`,
          ]}
        >
          <CommentFill comment={comment} topComment={comment.top_comment} />
        </div>
        {comment.child_comments && comment.child_comments.length > 0 && (
          <div tw='mt-2'>
            {comment.child_comments.map((subcomment, index) => {
              const subCommentRemoved =
                userCanModerate && moderationState.removedList.includes(subcomment.id);
              const subCommentBanned =
                userCanModerate && moderationState.bannedList.includes(subcomment.id);
              const subCommentHidden =
                userCanModerate && moderationState.hiddenList.includes(subcomment.id);
              const showChildComment = topThreeSubComments(
                hasHiddenChildComments,
                comment.child_comments,
              ).find((comment) => comment === subcomment.id);
              const subCommentCreatedAt = new Date(subcomment.creation_datetime);
              const isModeratedSub = moderatedAt >= subCommentCreatedAt;
              return (
                <div
                  key={index}
                  css={[
                    tw`py-2 ml-3 pl-3 flex flex-col border-l-4 border-brand transition-all 
                first-of-type:rounded-tr-lg last-of-type:rounded-br-lg first-of-type:border-b first-of-type:border-b-primary`,
                    subCommentRemoved &&
                      tw`bg-warning bg-opacity-20 pb-3 px-3 pt-2 border-l-4 border-warning`,
                    subCommentBanned &&
                      tw`bg-danger bg-opacity-20 pb-3 px-3 pt-2 border-l-4 border-danger`,
                    subCommentHidden &&
                      tw`bg-icons bg-opacity-20 pb-3 px-3 pt-2 border-l-4 border-icons`,
                    !subCommentRemoved &&
                      !subCommentBanned &&
                      !subCommentHidden &&
                      tw`bg-primary pb-3 pl-3 pt-2`,
                    !showChildComment &&
                      !showAllChildComments &&
                      hasHiddenChildComments &&
                      !activeModerationComments &&
                      tw`opacity-0 h-0 overflow-hidden p-0 ml-3`,
                    !showChildComment &&
                      showAllChildComments &&
                      hasHiddenChildComments &&
                      !userCanModerate &&
                      tw`bg-icons bg-opacity-20 opacity-100 h-full -mr-2 pr-2`,
                    (subcomment.newComment || subcomment.id === Number(lastAddedComment)) &&
                      tw`bg-info p-3 bg-opacity-20 rounded-r-lg`,
                  ]}
                  id={`comment${subcomment.id}`}
                >
                  <div
                    css={[
                      userCanModerate &&
                        commentsSort === 'Moderator view' &&
                        isModeratedSub &&
                        tw`opacity-50`,
                    ]}
                  >
                    <CommentFill
                      key={index}
                      comment={subcomment}
                      subComment
                      parentComment={comment}
                    />
                  </div>
                </div>
              );
            })}
            {hasHiddenChildComments && !activeModerationComments && (
              <ToggleChildComments
                childComments={comment.child_comments}
                numbOfChildCommentsShowing={numbOfChildCommentsShowing}
              />
            )}
          </div>
        )}
      </div>
      <hr tw='border-icons opacity-20 pt-0' />
    </CommentContext.Provider>
  );
});
